import * as THREE from "three";
import { FLOOR_Y, TILE } from "./layout";

/**
 * Expanding shockwave rings, for the moment a Slop is caught.
 *
 * A pool rather than per-event allocation: catching all four on one 5h Quota
 * Reset fires four of these within a second, and churning geometry mid-chase
 * is exactly when a frame drop is most visible.
 */

const POOL = 6;
const LIFETIME = 0.75;

interface Ring {
  readonly mesh: THREE.Mesh;
  age: number;
  active: boolean;
}

export class BurstField {
  readonly group = new THREE.Group();

  private readonly rings: Ring[] = [];
  private readonly geometry: THREE.RingGeometry;
  private readonly sparkGeometry: THREE.IcosahedronGeometry;

  constructor() {
    this.geometry = new THREE.RingGeometry(0.35, 0.52, 40);
    // Small and gold on purpose: at the ring's full 3.4x scale, bigger pale
    // sparks read as Story Points and the deck looks littered with collectables.
    this.sparkGeometry = new THREE.IcosahedronGeometry(0.05, 0);

    for (let i = 0; i < POOL; i += 1) {
      const material = new THREE.MeshBasicMaterial({
        color: 0xffc24d,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const holder = new THREE.Group();

      const ring = new THREE.Mesh(this.geometry, material);
      ring.rotation.x = -Math.PI / 2; // lie flat on the deck
      holder.add(ring);

      // A few sparks thrown outward, so the burst has some body to it rather
      // than reading as a flat decal.
      for (let s = 0; s < 6; s += 1) {
        const spark = new THREE.Mesh(this.sparkGeometry, material);
        const angle = (s / 6) * Math.PI * 2;
        spark.position.set(Math.cos(angle) * 0.5, 0.4, Math.sin(angle) * 0.5);
        holder.add(spark);
      }

      holder.visible = false;
      this.group.add(holder);
      this.rings.push({ mesh: holder as unknown as THREE.Mesh, age: 0, active: false });
    }
  }

  /** Fire a burst at a grid position. Reuses the oldest ring when saturated. */
  fire(gridX: number, gridY: number): void {
    const free = this.rings.find((r) => !r.active);
    const ring = free ?? this.rings.reduce((a, b) => (a.age > b.age ? a : b));
    ring.age = 0;
    ring.active = true;
    ring.mesh.visible = true;
    ring.mesh.position.set(gridX * TILE, FLOOR_Y + 0.35, gridY * TILE);
  }

  update(dt: number): void {
    for (const ring of this.rings) {
      if (!ring.active) continue;
      ring.age += dt;
      const t = ring.age / LIFETIME;
      if (t >= 1) {
        ring.active = false;
        ring.mesh.visible = false;
        continue;
      }
      // Ease out: fast punch then a slow fade, which reads as an impact.
      const eased = 1 - (1 - t) * (1 - t);
      ring.mesh.scale.setScalar(0.6 + eased * 3.4);
      ring.mesh.rotation.y = eased * 1.4;
      const material = (ring.mesh.children[0] as THREE.Mesh).material as THREE.MeshBasicMaterial;
      material.opacity = (1 - t) * 0.9;
    }
  }

  dispose(): void {
    this.geometry.dispose();
    this.sparkGeometry.dispose();
    for (const ring of this.rings) {
      const material = (ring.mesh.children[0] as THREE.Mesh).material as THREE.MeshBasicMaterial;
      material.dispose();
    }
  }
}
